import {
  ContractType,
  Country,
  Range,
  RealEstateImage,
  RealEstateImageFields,
  RealEstateObject,
  RealEstateObjectStatus,
  RealEstateResponse,
  RealEstateSourceObject,
  Subcategory,
} from '@immosky/types';
import {
  categoryFilterOptions,
  categoryToSubcategoriesMap,
} from '@immosky/object-search-filter';

interface LocationData {
  id: number;
  centroid: [number, number];
}

export interface QueryBuildInput {
  contractType?: ContractType;
  isResidia?: boolean;
  category?: {
    main?: string;
    sub?: Subcategory[];
  };
  price?: Range<number> | number;
  roomCount?: Range<number> | number;
  livingSpace?: Range<number> | number;
  usageSpace?: Range<number> | number;
  propertySpace?: Range<number> | number;
  status?: RealEstateObjectStatus;
  topOffer?: boolean;
  locations?: string | LocationData[];
  radius?: number;
  agent?: number;
}

export const selectedFields = [
  'title',
  'rooms',
  'postalCode',
  'city',
  'images',
  'images.url',
  'images.altText',
  'topImage.url',
  'topImage.altText',
  'location',
  'contractType',
  'subCategory',
  'category',
  'space.living',
  'space.property',
  'space.usage',
  'price.rentGross',
  'price.total',
  'flats',
  'offices',
  'topOffer',
  'status',
  'links.type',
  'links.url',
  'rateOfReturn',
];

export type SortOrder = 'asc' | 'desc';

export type Field =
  | 'category'
  | 'subCategory'
  // | 'countryIsocode'
  | 'rooms'
  | 'space'
  // | 'postalCode'
  // | 'city'
  | 'contractType'
  // | 'bedrooms'
  | 'location';

export type NestedField =
  | 'price.total'
  | 'price.rentGross'
  | 'space.living'
  | 'space.usable'
  | 'space.property'
  | 'estateAgent.id';

export type NestedFieldPath = 'price' | 'space' | 'estateAgent';
// | 'distances'
// | 'heating'
// | 'estateAgent'
// | 'links'
// | 'floorPlans'
// | 'images'
// | 'topImage'
// | 'description';

export type QuerySort =
  | { [K in NestedField]?: { order: SortOrder; nested: { path: string } } }
  | { [K in Field]?: SortOrder }
  | {
      _geo_distance: { [K in Field]?: number[][] } & {
        order: SortOrder;
        unit?: 'km';
        mode?: 'min';
        distance_type?: 'plane';
        ignore_unmapped?: boolean;
      };
    };

export type SortValue = { lat: number; lon: number };

export type UnnestedQueryPart =
  | { term: { [K in Field & NestedField]?: string | number } }
  | { terms: { [K in Field & NestedField]?: string[] | number[] } }
  | { range: { [K in Field & NestedField]?: { gte?: number; lte?: number } } }
  | { geo_shape: { [K in Field & NestedField]?: { indexed_shape: { index: string, id: string, path: string } } } }
  | { geo_distance: { distance: string, location: { lat: number, lon: number } } };

export type NextedQueryPart = {
  nested: {
    path: NestedFieldPath;
    query: UnnestedQueryPart;
  };
};

export type QueryPart = NextedQueryPart | UnnestedQueryPart;

export interface BoolQuery {
  bool: {
    must?: QueryPart[];
    must_not?: QueryPart[];
    should?: QueryPart[];
    filter?: QueryPart[];
  };
}

export class BoolQueryBuild {
  private isUsed = false;
  private query: BoolQuery = {
    bool: {},
  };

  must(q: QueryPart) {
    this.isUsed = true;
    if (!this.query.bool.must) {
      this.query.bool.must = [];
    }
    this.query.bool.must.push(q);
  }

  must_not(q: QueryPart) {
    this.isUsed = true;
    if (!this.query.bool.must_not) {
      this.query.bool.must_not = [];
    }
    this.query.bool.must_not.push(q);
  }

  should(q: QueryPart) {
    this.isUsed = true;
    if (!this.query.bool.should) {
      this.query.bool.should = [];
    }
    this.query.bool.should.push(q);
  }

  filter(q: QueryPart) {
    this.isUsed = true;
    if (!this.query.bool.filter) {
      this.query.bool.filter = [];
    }
    this.query.bool.filter.push(q);
  }

  build(): BoolQuery | Record<string, never> {
    if (this.isUsed) {
      return this.query;
    } else {
      return {};
    }
  }
}

export interface SearchQuery {
  from?: number;
  size?: number;
  sort?: QuerySort[];
  query: BoolQuery | Record<string, never>;
  fields: string[];
  _source: boolean;
}

export class QueryBuild {
  private input: QueryBuildInput = {};
  private bool = new BoolQueryBuild();
  private sort: QuerySort[] = [];
  private _limit?: { from: number; size: number };

  filter(input: QueryBuildInput, countries: Country[], regionsIndex="regions"): QueryBuild {
    this.appendCountries(countries);
    this.appendIsResidia(input);
    this.appendContractType(input);
    this.appendAgentId(input);
    this.appendCategory(input);
    this.appendPrice(input);
    this.appendRoomCount(input);
    this.appendLivingSpace(input);
    this.appendUsageSpace(input);
    this.appendPropertySpace(input);
    this.appendStatus(input);
    this.appendTopOffer(input);
    this.appendLocations(input, regionsIndex);
    this.input = {
      ...this.input,
      ...input,
    };
    return this;
  }

  limit(size: number, from: number = 0): QueryBuild {
    this._limit = { from, size };
    return this;
  }

  order_by(
    field_name: Field | NestedField | 'price',
    order: SortOrder = 'asc',
    value?: SortValue
  ): QueryBuild {
    if (field_name === 'location' && value) {
      this.sort.push({
        _geo_distance: {
          [field_name]: [[value.lon, value.lat]],
          order,
          unit: 'km',
        },
      });
    } else {
      if (field_name === 'price') {
        field_name =
          this.input.contractType === 'rent'
            ? 'price.rentGross'
            : 'price.total';
      }
      const [path, nested_field] = field_name.split(/\.(.+)/);
      if (nested_field) {
        this.sort.push({
          [field_name]: {
            order,
            nested: { path },
          },
        });
      } else {
        this.sort.push({ [field_name]: order });
      }
    }
    return this;
  }

  private appendLocations(input: QueryBuildInput, regionsIndex: string) {
    const locations = input.locations as LocationData[]
    const radius: undefined | number = input.radius

    if(locations && locations.length > 0) {
      let geoFilters: QueryPart[] = []

      if(locations.length === 1 && radius) {
        geoFilters.push({
          geo_distance: {
            distance: `${radius}km`,
            location: {lat: locations[0].centroid[1], lon: locations[0].centroid[0]}
          }
        })
      }
      else {
        geoFilters = locations.map((elt: any) => {
          return {
            geo_shape: {
              location: {
                indexed_shape: {index: regionsIndex, id: elt.id, path: "geometry"}}
            }
          }
        })
      }

      geoFilters.forEach((filter) => {
        this.bool.should(filter)
      })
    }
    return this
  }
  private appendCountries(countries: Country[]) {
    if (countries) {
      const filter = {
        terms: {
          countryIsocode: countries,
        },
      };

      this.bool.must(filter);
    }
  }

  private appendIsResidia(input: QueryBuildInput) {
    if(input.isResidia) {
      this.bool.must({ term: { isResidia: input.isResidia } });
    } else {
      this.bool.must_not({ term: { isResidia: true } });
    }
  }

  private appendContractType(input: QueryBuildInput) {
    if (input.contractType) {
      this.bool.must({ term: { contractType: input.contractType } });
    }
  }

  private appendAgentId(input: QueryBuildInput) {
    if (input.agent) {
      this.appendNestedRangeOrValue(input.agent, 'estateAgent', 'estateAgent.id');
    }
  }

  private appendCategory(input: QueryBuildInput) {
    if (input.category && input.category.main) {
      this.bool.must({ term: { category: input.category.main } });
    }
    if (input.category && input.category.sub && input.category.sub.length > 0) {
      this.bool.must({ terms: { subCategory: input.category.sub } });
    }
  }

  private appendNestedRangeOrValue<T>(
    value: Range<T> | T,
    path: NestedFieldPath,
    field_name: NestedField
  ) {
    if (typeof value === 'number' || typeof value === 'string') {
      this.bool.must({
        nested: {
          path: path,
          query: {
            term: {
              [field_name]: value,
            },
          },
        },
      });
    } else if ((value as Range<T>).min || (value as Range<T>).max) {
      this.bool.must({
        nested: {
          path: path,
          query: {
            range: {
              [field_name]: {
                ...((value as Range<T>).min
                  ? { gte: (value as Range<T>).min }
                  : {}),
                ...((value as Range<T>).max
                  ? { lte: (value as Range<T>).max }
                  : {}),
              },
            },
          },
        },
      });
    }
  }

  private appendRangeOrValue<T>(value: Range<T> | T, field_name: Field) {
    if (typeof value === 'number' || typeof value === 'string') {
      this.bool.must({
        term: {
          [field_name]: value,
        },
      });
    } else if ((value as Range<T>).min || (value as Range<T>).max) {
      this.bool.must({
        range: {
          [field_name]: {
            ...((value as Range<T>).min
              ? { gte: (value as Range<T>).min }
              : {}),
            ...((value as Range<T>).max
              ? { lte: (value as Range<T>).max }
              : {}),
          },
        },
      });
    }
  }

  private appendPrice(input: QueryBuildInput) {
    if (input.price) {
      const field_name: NestedField =
        input.contractType === 'rent' ? 'price.rentGross' : 'price.total';
      this.appendNestedRangeOrValue(input.price, 'price', field_name);
    }
  }

  private appendRoomCount(input: QueryBuildInput) {
    if (input.roomCount) {
      this.appendRangeOrValue(input.roomCount, 'rooms');
    }
  }

  private appendLivingSpace(input: QueryBuildInput) {
    if (input.livingSpace) {
      this.appendNestedRangeOrValue(input.livingSpace, 'space', 'space.living');
    }
  }

  private appendUsageSpace(input: QueryBuildInput) {
    if (input.usageSpace) {
      this.appendNestedRangeOrValue(input.usageSpace, 'space', 'space.usable');
    }
  }

  private appendPropertySpace(input: QueryBuildInput) {
    if (input.propertySpace) {
      this.appendNestedRangeOrValue(
        input.propertySpace,
        'space',
        'space.property'
      );
    }
  }

  private appendStatus(input: QueryBuildInput) {
    if (input.status) {
      this.bool.must({
        term: {
          status: input.status,
        },
      });
    } else {
      this.bool.must_not({
        term: {
          status: 'sold',
        },
      });
    }
  }

  private appendTopOffer(input: QueryBuildInput) {
    if (input.topOffer) {
      this.bool.must({
        term: {
          topOffer: true,
        },
      });
    }
  }

  build(): SearchQuery {
    return {
      ...(this._limit ? this._limit : {}),
      ...(this.sort.length > 0 ? { sort: this.sort } : {}),
      query: {
        ...this.bool.build(),
      },
      fields: selectedFields,
      _source: false,
    };
  }
}

function normalizeImageFields(
  image: RealEstateImageFields | RealEstateImage
): RealEstateImage {
  return {
    url: Array.isArray(image.url) ? image.url[0] : image.url,
    altText: Array.isArray(image.altText)
      ? image.altText[0] || ''
      : image.altText || '',
  };
}

export function convertResponseToRealEstateObjects(
  response: RealEstateResponse
): RealEstateObject[] {
  const sourceObjects: RealEstateSourceObject[] = response.hits.hits.map(
    (hit) => {
      const categories = hit.fields.category;
      const mainCategory = categories[0];
      let isAlsoIncomeProperty = false;

      if (categories.length > 1) {
        isAlsoIncomeProperty =
          mainCategory !== 'income' && categories[1] === 'income';
      }

      return {
        id: hit._id,
        title: (hit.fields.title && hit.fields.title.length) ? hit.fields.title[0] : '',
        rooms: hit.fields.rooms ? hit.fields.rooms[0] : undefined,
        postalCode: hit.fields.postalCode
          ? hit.fields.postalCode[0]
          : undefined,
        city: hit.fields.city ? hit.fields.city[0] : undefined,
        space: {
          living:
            hit.fields.space && hit.fields.space[0].living
              ? hit.fields.space[0].living[0]
              : undefined,
          property:
            hit.fields.space && hit.fields.space[0].property
              ? hit.fields.space[0].property[0]
              : undefined,
          usage:
            hit.fields.space && hit.fields.space[0].usage
              ? hit.fields.space[0].usage[0]
              : undefined,
        },
        price: {
          rentGross:
            hit.fields.price && hit.fields.price[0].rentGross
              ? hit.fields.price[0].rentGross[0]
              : undefined,
          total:
            hit.fields.price && hit.fields.price[0].total
              ? hit.fields.price[0].total[0]
              : undefined,
        },
        images: hit.fields.images?.map(normalizeImageFields),
        topImage: hit.fields.topImage?.[0]
          ? normalizeImageFields(hit.fields.topImage[0])
          : undefined,
        location: {
          lat: hit.fields.location[0].coordinates[1],
          lon: hit.fields.location[0].coordinates[0],
        },
        contractType: hit.fields.contractType[0],
        subCategory: hit.fields.subCategory[0],
        category: mainCategory,
        isAlsoIncomeProperty: isAlsoIncomeProperty,
        flats: hit.fields.flats ? hit.fields.flats[0] : undefined,
        offices: hit.fields.offices ? hit.fields.offices[0] : undefined,
        topOffer: hit.fields.topOffer[0],
        status: hit.fields.status[0],
        hasVirtualTour:
          hit.fields.links.filter(
            (links) => links.type[0] === 'virtual-tour' && links.url
          ).length > 0,
        rateOfReturn: hit.fields.rateOfReturn
          ? hit.fields.rateOfReturn[0]
          : undefined,
      };
    }
  );

  return sourceObjects.map<RealEstateObject>(
    (source: RealEstateSourceObject) => {
      const images = source.images || [];

      const topImage: RealEstateImage = {
        url: source.topImage?.url || '',
        altText: source.topImage?.altText || '',
      };

      const categoryLabel = categoryFilterOptions.allOptions.find(
        (x) => x.value === source.category
      )?.text;

      const subCategoryLabel = categoryToSubcategoriesMap[source.category].find(
        (x) => x.value === source.subCategory
      )?.text;

      return {
        images,
        topImage,
        location: {
          city: source.city || '',
          postCode: source.postalCode || '',
          coordinates: {
            lat: source.location.lat,
            lng: source.location.lon,
          },
        },
        priceOnRequest: !source.price.total,
        price: {
          rentGross: source.price.rentGross || 0,
          total: source.price.total || 0,
        },
        floorSpace: source.space.living || 0,
        propertySpace: source.space.property || 0,
        usageSpace: source.space.usage || 0,
        roomCount: source.rooms || '',
        title: subCategoryLabel || categoryLabel || '',
        description: source.title || '',
        id: source.id || '',
        contractType: source.contractType,
        flats: source.flats || 0,
        offices: source.offices || 0,
        category: source.category,
        subCategory: source.subCategory,
        isAlsoIncomeProperty: source.isAlsoIncomeProperty,
        topOffer: source.topOffer,
        status: source.status,
        hasVirtualTour: source.hasVirtualTour,
        rateOfReturn: source.rateOfReturn || null,
      };
    }
  );
}
